/**
 * The single "Connection failed" toast for this app's link to the server,
 * run by whichever window owns that socket (the desktop owner webview, or
 * the web workspace page). The socket keeps retrying on its own and only
 * reports `failed` after a minute down (`FAILED_AFTER_MS` in
 * `@/api/ws-outage-clock`), so a brief backend restart shows nothing. The
 * toast is an ordinary system toast, dismissible like every other one. It
 * clears as soon as the socket reconnects or its owner unmounts, gives way
 * to the actionable companion disconnect toast during a shared outage, and
 * a dismissal holds until the next outage.
 */
import { useEffect, useRef } from "react";
import { translate } from "@/i18n";
import { desktopConnectionHealthMessages } from "@/i18n/messages/desktop-connection-health";
import { useCompanionStore } from "@/stores/companion-store";
import { useNotificationStore } from "@/stores/notification-store";
import { COMPANION_DISCONNECTED_TOAST_ID } from "./companion-disconnect-toast";

const SERVER_UNREACHABLE_TOAST_ID = "server-unreachable";

function clearToast(): void {
  useNotificationStore
    .getState()
    .removeSystemToast(SERVER_UNREACHABLE_TOAST_ID);
}

export function useServerUnreachableToast({
  enabled,
}: {
  enabled: boolean;
}): void {
  const failed = useCompanionStore(
    (s) => enabled && s.wsConnectionState === "failed",
  );
  const companionToastShown = useNotificationStore((s) =>
    s.systemToasts.some(
      (toast) => toast.id === COMPANION_DISCONNECTED_TOAST_ID,
    ),
  );
  // One notice per outage: once shown (or covered by the companion toast),
  // a dismissal is final until the socket reconnects.
  const noticedThisOutage = useRef(false);

  useEffect(() => {
    if (!failed) {
      noticedThisOutage.current = false;
      clearToast();
      return;
    }
    if (companionToastShown) {
      noticedThisOutage.current = true;
      clearToast();
      return;
    }
    if (noticedThisOutage.current) return;
    noticedThisOutage.current = true;
    useNotificationStore.getState().addSystemToast(() => ({
      id: SERVER_UNREACHABLE_TOAST_ID,
      level: "error",
      icon: { kind: "icon", name: "WifiOff" },
      title: translate(
        desktopConnectionHealthMessages,
        "serverUnreachableTitle",
      ),
      description: translate(
        desktopConnectionHealthMessages,
        "serverUnreachableBody",
      ),
      persistent: true,
    }));
  }, [failed, companionToastShown]);

  useEffect(() => clearToast, []);
}
